import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from './session';
import './auth.css';

/**
 * Sign-in — email and password (D-005).
 *
 * BUILT FROM THE DESIGN SYSTEM, NOT COPIED FROM A SCREEN. The design export
 * lists "S01 Launch / auth" in its screen inventory but never renders it — the
 * prototype contains no sign-in markup at all. So this screen is assembled from
 * the locked tokens, type scale, control sizes and component vocabulary the
 * export does define, rather than invented visuals. Flagged in the handoff.
 *
 * There is deliberately NO "create account" affordance anywhere: public
 * self-registration is disabled and Brandon's account is provisioned (D-005).
 */
export function SignInScreen() {
  const { signIn, unconfigured } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    const result = await signIn(email, password);
    setBusy(false);
    if (result.error) setError(result.error);
  }

  return (
    <main className="cl-screen cl-centered auth-screen">
      <header className="auth-header">
        <h1 className="auth-wordmark">Crewlink MemorySite</h1>
        <p className="auth-subtitle">Sign in to reach your Projects.</p>
      </header>

      {unconfigured ? (
        <p className="cl-notice" role="status">
          Crewlink is not connected to a server yet, so signing in is not possible on this build.
        </p>
      ) : null}

      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <label className="cl-field">
          <span className="cl-label">Email</span>
          <input
            className="cl-input"
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={unconfigured || busy}
          />
        </label>

        <label className="cl-field">
          <span className="cl-label">Password</span>
          <input
            className="cl-input"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={unconfigured || busy}
          />
        </label>

        {error ? (
          <p className="cl-error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="auth-actions">
          <button className="cl-button" type="submit" disabled={unconfigured || busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </div>
      </form>

      <p className="auth-footer">
        <Link className="cl-link" to="/forgot-password">
          Forgot your password?
        </Link>
      </p>
    </main>
  );
}
